import { QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import {
  Account,
  assertNever,
  AuthConfig,
  BehavesAsControlCodeMappings,
  capitalize,
  Control,
  ControlCategories,
  ControlCategory,
  ControlCode,
  ControlMonitorEndpointControlSummary,
  ControlMonitorInboxControlSummary,
  ControlMonitorSummaryResponse,
  ControlMonitorUserControlSummary,
  getCategoryControls,
  getControlCategory,
  getControlCategoryName,
  getControlName,
  getPartnerEventName,
  getRFMDescription,
  isDefensiveControl,
  isEmailControl,
  isIdentityProviderControl,
  isTruthy,
  isVulnerabilityManagementControl,
  ObjectException,
  ODataQuery,
  parseDate,
  PartnerEvents,
  PolicyException,
  queries,
  SCMThreat,
  service,
  useAuthConfig,
  ValidControlCategory,
  ValidControlCode,
  ValidPartnerEventCode,
} from "@theprelude/core";
import { useExceptionVisibilityStore } from "@/components/control-monitor/use-exception-visibility";
import { exceptedOdata } from "@/components/filter-form/additional-filters";
import { getOdataFilters } from "@/components/filter-form/odata";
import { Category } from "@/components/filter-form/top-level-form";
import { getIntegrationForm } from "@/components/integrations/integration-overlay";
import { Filter, TopLevelFilters } from "@/lib/filter";

export type ResourceType = "endpoint" | "user" | "inbox";

// DataPointBase represents any bubble in the control validation hierarchy.
type DataPointBase = {
  /** A unique type for a collection of data points. */
  type: string;
  /** A unique identifier for each individual data point. */
  id: string;
  /** The display name for a data point. */
  name: string;
  /** The control code associated with this data point. */
  control?: ControlCode;
  /** The control instance identifier associated with this data point. */
  instanceId?: string;
  /** Whether the data point is interactive. */
  enabled: boolean;
  /** The type of resource being counted for presentation. */
  resourceType: ResourceType;
  /** The total number of resources for this data point. */
  resourceCount: number;
  /** The total number of control failures for this data point. */
  controlFailureCount: number;
  /** The total number of misconfigurations for this data point. */
  misconfigurationCount: number;
  /** The total number of exceptions for this data point. */
  exceptionCount: number;
  /** Any data points nested under this data point (e.g.: policies under controls). */
  children: DataPoint[];
};

export type RootDataPoint = DataPointBase & {
  type: "root";
  id: "my-controls";
  totalEndpointCount: number;
  totalUserCount: number;
  totalInboxCount: number;
  exceptedResourceCount: number;
  exceptedEndpointCount: number;
  exceptedUserCount: number;
  exceptedInboxCount: number;
};

export type NoControlsDataPoint = DataPointBase & {
  type: "no-controls";
};

export type ConnectedControlDataPoint = DataPointBase & {
  type: "connected-control";
  control: ValidControlCode;
  instanceId: string;
  instanceName: string | undefined;
  category: ControlCategory;
  settingsCount: number;
  settingsMisconfigurationCount: number;
};

export type DisconnectedControlDataPoint = DataPointBase & {
  type: "disconnected-control";
  control: ValidControlCode;
  category: ControlCategory;
  children: never[];
};

export type ControlCategoryDataPoint = DataPointBase & {
  type: "control-category";
  id:
    | "cloud"
    | "discovered-devices"
    | "edr"
    | "email"
    | "endpoint-management"
    | "identity-provider"
    | "network"
    | "private-repository"
    | "siem"
    | "vulnerability-management";
  category: ControlCategory;
};

export type BucketDataPoint = DataPointBase & {
  type: "bucket";
  id: `${string}-control-failures` | `${string}-policy-misconfigurations`;
  instanceId: string;
  control: ValidControlCode;
};

export type ComingSoonDataPoint = DataPointBase & {
  type: "coming-soon";
  children: never[];
};

export type ControlCategoryFailureDataPoint = DataPointBase & {
  type: "control-category-failure";
  id:
    | "no-edr"
    | "no-endpoint-management"
    | `${string}-missing-agent`
    | `${string}-missing-scan`;
  category: ValidControlCategory;
  failure: ValidPartnerEventCode;
  children: never[];
};

export type ControlFailureDataPoint = DataPointBase & {
  type: "control-failure";
  id:
    | `${string}-reduced-functionality-mode`
    | `${string}-no-edr-policy`
    | `${string}-no-av-policy`
    | `${string}-missing-agent`
    | `${string}-missing-mfa`
    | `${string}-missing-scan`
    | `${string}-no-registered-devices`
    | `${string}-out-of-date-scan`;
  instanceId: string;
  control: ValidControlCode;
  failure: ValidPartnerEventCode;
  children: never[];
};

export type DataPoint =
  | RootDataPoint
  | ConnectedControlDataPoint
  | ComingSoonDataPoint
  | ControlCategoryDataPoint
  | DisconnectedControlDataPoint
  | NoControlsDataPoint
  | BucketDataPoint
  | ControlCategoryFailureDataPoint
  | ControlFailureDataPoint;

const sum = (arr: number[]) => arr.reduce((sum, num) => sum + num, 0);

export const getCategorySlug = (
  category: ValidControlCategory,
): ControlCategoryDataPoint["id"] => {
  switch (category) {
    case ControlCategories.ASSET_MANAGER: {
      return "endpoint-management";
    }
    case ControlCategories.CLOUD: {
      return "cloud";
    }
    case ControlCategories.DISCOVERED_DEVICES: {
      return "discovered-devices";
    }
    case ControlCategories.EMAIL: {
      return "email";
    }
    case ControlCategories.IDENTITY: {
      return "identity-provider";
    }
    case ControlCategories.NETWORK: {
      return "network";
    }
    case ControlCategories.PRIVATE_REPO: {
      return "private-repository";
    }
    case ControlCategories.SIEM: {
      return "siem";
    }
    case ControlCategories.XDR: {
      return "edr";
    }
    case ControlCategories.VULN_MANAGER: {
      return "vulnerability-management";
    }
    default: {
      return assertNever(category);
    }
  }
};

export const getControlSlug = (control: ControlCode) =>
  getControlName(control).toLowerCase().replaceAll(/\W+/gi, "-");

export const getControlId = ({
  controls,
  control,
  category,
  instanceId,
}: {
  controls: Control[];
  control: ControlCode;
  category: ValidControlCategory;
  instanceId: string;
}) => {
  const controlCount = controls.filter((c) => c.id === control).length;
  return controlCount > 1
    ? `${getCategorySlug(category)}-${getControlSlug(control)}-${instanceId}`
    : `${getCategorySlug(category)}-${getControlSlug(control)}`;
};

const getSummaryCount = (
  summary: ControlMonitorSummaryResponse,
  category: ControlCategory,
  showExcepted: boolean,
) => {
  const endpointCategory = summary.endpoint_summary.categories.find(
    (c) => c.category === category,
  );
  const inboxCategory = summary.inbox_summary.categories.find(
    (c) => c.category === category,
  );
  const userCategory = summary.user_summary.categories.find(
    (c) => c.category === category,
  );

  return showExcepted
    ? (endpointCategory?.endpoint_count ??
        inboxCategory?.inbox_count ??
        userCategory?.user_count ??
        0)
    : (endpointCategory?.excepted?.endpoint_count ??
        inboxCategory?.excepted.inbox_count ??
        userCategory?.excepted.user_count ??
        0);
};

const getAggregateCounts = (data: DataPoint[]) => {
  const items = data.filter((d) => d.enabled);
  const controlFailureCount = sum(items.map((i) => i.controlFailureCount));
  const misconfigurationCount = sum(items.map((i) => i.misconfigurationCount));
  return { controlFailureCount, misconfigurationCount };
};

const getCategoryExceptionsCount = ({
  category,
  objectExceptions,
  policyExceptions,
}: {
  category: ControlCategory;
  objectExceptions: ObjectException[];
  policyExceptions: PolicyException[];
}) =>
  objectExceptions.filter(
    (e) =>
      e.category === category &&
      (!e.expires || parseDate(e.expires) >= new Date()),
  ).length +
  policyExceptions.filter(
    (e) =>
      getControlCategory(e.control) === category &&
      (!e.expires || parseDate(e.expires) >= new Date()),
  ).length;

interface FormattedDataOptions {
  account: Account;
  summary: ControlMonitorSummaryResponse;
  objectExceptions: ObjectException[];
  policyExceptions: PolicyException[];
  threat?: SCMThreat;
  technique?: string;
  showExcepted: boolean;
}

const getControlsData = ({
  category,
  account,
  summary,
  policyExceptions,
  threat,
  technique,
  showExcepted,
}: FormattedDataOptions & { category: ValidControlCategory }) => {
  const controls = getCategoryControls(category);

  const getCount = <
    T extends
      | ControlMonitorEndpointControlSummary
      | ControlMonitorInboxControlSummary
      | ControlMonitorUserControlSummary,
  >(
    summary: T | undefined,
    key: keyof T & keyof T["excepted"],
  ): number => {
    // @ts-expect-error -- not sure why we're getting a type error here
    return (showExcepted ? summary?.[key] : summary?.excepted[key]) ?? 0;
  };

  return controls.flatMap((control): DataPoint | DataPoint[] => {
    const name = getControlName(control);
    let id = `${getCategorySlug(category)}-${getControlSlug(control)}`;

    const behavesAsControl = BehavesAsControlCodeMappings[control] ?? control;

    const instances = account.controls.filter((c) => c.id === behavesAsControl);

    if (instances.length === 0) {
      return getIntegrationForm(control)
        ? {
            type: "disconnected-control" as const,
            id,
            name,
            enabled: !threat,
            control,
            category,
            resourceType: "endpoint",
            resourceCount: 0,
            controlFailureCount: 0,
            misconfigurationCount: 0,
            exceptionCount: 0,
            children: [],
          }
        : {
            type: "coming-soon" as const,
            id,
            name,
            enabled: !threat,
            control,
            resourceType: "endpoint",
            resourceCount: 0,
            controlFailureCount: 0,
            misconfigurationCount: 0,
            exceptionCount: 0,
            children: [],
          };
    }

    return instances.map((instance): DataPoint => {
      const endpointSummary = summary.endpoint_summary.categories
        .find((c) => c.category === category)
        ?.instances.find(
          (s) =>
            s.control === control && s.instance_id === instance.instance_id,
        );
      const userSummary = summary.user_summary.categories
        .find((c) => c.category === category)
        ?.instances.find(
          (s) =>
            s.control === control && s.instance_id === instance.instance_id,
        );
      const inboxSummary = summary.inbox_summary.categories
        .find((c) => c.category === category)
        ?.instances.find(
          (s) =>
            s.control === control && s.instance_id === instance.instance_id,
        );

      const endpointCount = getCount(endpointSummary, "endpoint_count");
      const userCount = getCount(userSummary, "user_count");
      const inboxCount = getCount(inboxSummary, "inbox_count");

      const settingsCount =
        endpointSummary?.setting_count ||
        userSummary?.setting_count ||
        inboxSummary?.setting_count ||
        0;

      // Policy failures
      const misconfigurationCount =
        getCount(endpointSummary, "setting_misconfiguration_count") ||
        getCount(userSummary, "setting_misconfiguration_count") ||
        getCount(inboxSummary, "setting_misconfiguration_count");

      // EDR failures
      const noAVPolicyCounts = getCount(endpointSummary, "no_av_policy");
      const noEDRPolicyCounts = getCount(endpointSummary, "no_edr_policy");
      const reducedFunctionalityModeCounts = getCount(
        endpointSummary,
        "reduced_functionality_mode",
      );

      // Identity Provider failures
      const missingMFACount = getCount(userSummary, "missing_mfa_count");
      const noRegisteredDevicesCount = getCount(
        userSummary,
        "all_endpoint_failure_count",
      );

      // Vulnerability Management failures
      const missingAgentCount = getCount(
        endpointSummary,
        "missing_agent_count",
      );
      const missingScanCount = getCount(endpointSummary, "missing_scan_count");
      const outOfDateScanCount = getCount(
        endpointSummary,
        "out_of_date_scan_count",
      );

      const controlFailureCount =
        getCount(endpointSummary, "control_failure_count") +
        getCount(userSummary, "control_failure_count");

      const resourceType: ResourceType = isIdentityProviderControl(control)
        ? "user"
        : isEmailControl(control)
          ? "inbox"
          : "endpoint";

      const resourceCount = isIdentityProviderControl(control)
        ? userCount
        : isEmailControl(control)
          ? inboxCount
          : endpointCount;

      id = getControlId({
        controls: account.controls,
        category,
        control: instance.id,
        instanceId: instance.instance_id,
      });

      const controlFailures: DataPoint[] = [
        isDefensiveControl(control) &&
          endpointSummary?.reduced_functionality_mode !== null && {
            id: `${id}-reduced-functionality-mode` as const,
            type: "control-failure" as const,
            name: getRFMDescription(control),
            failure: PartnerEvents.REDUCED_FUNCTIONALITY_MODE,
            enabled: !(
              threat &&
              technique &&
              reducedFunctionalityModeCounts === 0
            ),
            instanceId: instance.instance_id,
            control,
            category,
            resourceType: "endpoint" as const,
            resourceCount: reducedFunctionalityModeCounts,
            controlFailureCount: reducedFunctionalityModeCounts,
            misconfigurationCount: 0,
            exceptionCount: 0,
            children: [],
          },
        isDefensiveControl(control) &&
          endpointSummary?.no_av_policy !== null && {
            id: `${id}-no-av-policy` as const,
            type: "control-failure" as const,
            name: "No AV Policies",
            failure: PartnerEvents.MISSING_AV_POLICY,
            enabled: !(threat && technique && noAVPolicyCounts === 0),
            instanceId: instance.instance_id,
            control,
            category,
            resourceType: "endpoint" as const,
            resourceCount: noAVPolicyCounts,
            controlFailureCount: noAVPolicyCounts,
            misconfigurationCount: 0,
            exceptionCount: 0,
            children: [],
          },
        isDefensiveControl(control) &&
          endpointSummary?.no_edr_policy !== null && {
            id: `${id}-no-edr-policy` as const,
            type: "control-failure" as const,
            name: "No EDR Policies",
            failure: PartnerEvents.MISSING_EDR_POLICY,
            enabled: !(threat && technique && noEDRPolicyCounts === 0),
            instanceId: instance.instance_id,
            control,
            category,
            resourceType: "endpoint" as const,
            resourceCount: noEDRPolicyCounts,
            controlFailureCount: noEDRPolicyCounts,
            misconfigurationCount: 0,
            exceptionCount: 0,
            children: [],
          },
        isVulnerabilityManagementControl(control) && {
          id: `${id}-${instance.instance_id}-missing-agent` as const,
          type: "control-failure" as const,
          name: getPartnerEventName(PartnerEvents.NO_VULN_MANAGER),
          failure: PartnerEvents.NO_VULN_MANAGER,
          enabled: !(threat && technique),
          instanceId: instance.instance_id,
          control,
          category,
          resourceType: "endpoint" as const,
          resourceCount: missingAgentCount,
          controlFailureCount: missingAgentCount,
          misconfigurationCount: 0,
          exceptionCount: 0,
          children: [],
        },
        isVulnerabilityManagementControl(control) && {
          id: `${id}-${instance.instance_id}-missing-scan` as const,
          type: "control-failure" as const,
          name: getPartnerEventName(PartnerEvents.MISSING_SCAN),
          failure: PartnerEvents.MISSING_SCAN,
          enabled: !(threat && technique && missingScanCount === 0),
          instanceId: instance.instance_id,
          control,
          category,
          resourceType: "endpoint" as const,
          resourceCount: missingScanCount,
          controlFailureCount: missingScanCount,
          misconfigurationCount: 0,
          exceptionCount: 0,
          children: [],
        },
        isVulnerabilityManagementControl(control) && {
          id: `${id}-${instance.instance_id}-out-of-date-scan` as const,
          type: "control-failure" as const,
          name: getPartnerEventName(PartnerEvents.OUT_OF_DATE_SCAN),
          failure: PartnerEvents.OUT_OF_DATE_SCAN,
          enabled: !(threat && technique && outOfDateScanCount === 0),
          instanceId: instance.instance_id,
          control,
          category,
          resourceType: "endpoint" as const,
          resourceCount: outOfDateScanCount,
          controlFailureCount: outOfDateScanCount,
          misconfigurationCount: 0,
          exceptionCount: 0,
          children: [],
        },
        isIdentityProviderControl(control) && {
          id: `${id}-no-registered-devices` as const,
          type: "control-failure" as const,
          name: "No Registered Devices",
          failure: PartnerEvents.USER_MISSING_EDR,
          enabled: !(threat && technique && noRegisteredDevicesCount === 0),
          instanceId: instance.instance_id,
          control,
          category,
          resourceType: "user" as const,
          resourceCount: noRegisteredDevicesCount,
          controlFailureCount: noRegisteredDevicesCount,
          misconfigurationCount: 0,
          exceptionCount: 0,
          children: [],
        },
        isIdentityProviderControl(control) && {
          id: `${id}-missing-mfa` as const,
          type: "control-failure" as const,
          name: "Missing MFA",
          failure: PartnerEvents.MISSING_MFA,
          enabled: !(threat && technique && missingMFACount === 0),
          instanceId: instance.instance_id,
          control,
          category,
          resourceType: "user" as const,
          resourceCount: missingMFACount,
          controlFailureCount: missingMFACount,
          misconfigurationCount: 0,
          exceptionCount: 0,
          children: [],
        },
      ].filter(isTruthy);

      const children: DataPoint[] = [
        (isDefensiveControl(control) ||
          isIdentityProviderControl(control) ||
          isVulnerabilityManagementControl(control)) && {
          type: "bucket" as const,
          id: `${id}-control-failures` as const,
          name: "Control Failures",
          enabled: controlFailures.some((c) => c.enabled),
          instanceId: instance.instance_id,
          control,
          resourceType,
          resourceCount: controlFailureCount,
          controlFailureCount,
          misconfigurationCount: 0,
          exceptionCount: 0,
          children: controlFailures,
        },
        (isDefensiveControl(control) ||
          isEmailControl(control) ||
          isIdentityProviderControl(control)) && {
          type: "bucket" as const,
          id: `${id}-policy-misconfigurations` as const,
          name: "Policy Configurations",
          enabled: !threat || settingsCount > 0,
          instanceId: instance.instance_id,
          control,
          resourceType,
          resourceCount,
          controlFailureCount: 0,
          misconfigurationCount,
          exceptionCount: policyExceptions.filter(
            (e) =>
              e.control === control &&
              e.instance_id === instance.instance_id &&
              (!e.expires || parseDate(e.expires) >= new Date()),
          ).length,
          children: [],
        },
      ].filter(isTruthy);

      const aggregateCounts =
        children.length === 0
          ? { controlFailureCount, misconfigurationCount }
          : getAggregateCounts(children);

      return {
        type: "connected-control" as const,
        id,
        name,
        enabled: !threat || children.some((c) => c.enabled),
        instanceId: instance.instance_id,
        instanceName: instance.name?.trim() || undefined,
        control,
        category,
        resourceType,
        resourceCount:
          resourceType === "inbox"
            ? inboxCount
            : resourceType === "user"
              ? userCount
              : endpointCount,
        settingsCount,
        settingsMisconfigurationCount: misconfigurationCount,
        controlFailureCount: aggregateCounts.controlFailureCount,
        misconfigurationCount: aggregateCounts.misconfigurationCount,
        exceptionCount: 0,
        children,
      };
    });
  });
};

const getDiscoveredDevicesBucket = (
  options: FormattedDataOptions,
): DataPoint => {
  const { summary, objectExceptions, policyExceptions, showExcepted } = options;

  const category = ControlCategories.DISCOVERED_DEVICES;
  const categoryName = getControlCategoryName(category);
  const children = getControlsData({ ...options, category });
  const endpointCount = getSummaryCount(summary, category, showExcepted);

  const { controlFailureCount, misconfigurationCount } =
    getAggregateCounts(children);

  return {
    type: "control-category",
    id: getCategorySlug(category),
    name: categoryName,
    category,
    enabled: children.some((c) => c.enabled),
    resourceType: "endpoint",
    resourceCount: endpointCount,
    controlFailureCount,
    misconfigurationCount,
    exceptionCount: getCategoryExceptionsCount({
      category,
      objectExceptions,
      policyExceptions,
    }),
    children,
  };
};

const getEDRBucket = (options: FormattedDataOptions): DataPoint => {
  const {
    summary,
    objectExceptions,
    policyExceptions,
    threat,
    technique,
    showExcepted,
  } = options;

  const category = ControlCategories.XDR;
  const categoryName = getControlCategoryName(category);

  const missingCount =
    (showExcepted
      ? summary.endpoint_summary.missing_edr_count
      : summary.endpoint_summary.excepted.missing_edr_count) ?? 0;

  const noEDR: ControlCategoryFailureDataPoint = {
    type: "control-category-failure",
    id: "no-edr",
    name: getPartnerEventName(PartnerEvents.NO_EDR),
    enabled: !(threat && technique),
    category,
    failure: PartnerEvents.NO_EDR,
    resourceType: "endpoint",
    resourceCount: missingCount,
    controlFailureCount: 0,
    misconfigurationCount: missingCount,
    exceptionCount: 0,
    children: [],
  };

  const controlItems = getControlsData({ ...options, category });

  const children = [noEDR, ...controlItems];

  const endpointCount = getSummaryCount(
    summary,
    ControlCategories.XDR,
    showExcepted,
  );

  const { controlFailureCount, misconfigurationCount } =
    getAggregateCounts(children);

  return {
    type: "control-category",
    id: getCategorySlug(category),
    name: categoryName,
    category,
    enabled: children.some((c) => c.enabled),
    resourceType: "endpoint",
    resourceCount: endpointCount,
    controlFailureCount,
    misconfigurationCount,
    exceptionCount: getCategoryExceptionsCount({
      category,
      objectExceptions,
      policyExceptions,
    }),
    children,
  };
};

const getEndpointManagementBucket = (
  options: FormattedDataOptions,
): DataPoint => {
  const {
    summary,
    objectExceptions,
    policyExceptions,
    threat,
    technique,
    showExcepted,
  } = options;

  const category = ControlCategories.ASSET_MANAGER;
  const categoryName = getControlCategoryName(category);

  const missingCount =
    (showExcepted
      ? summary.endpoint_summary.missing_asset_manager_count
      : summary.endpoint_summary.excepted.missing_asset_manager_count) ?? 0;

  const noEndpointManagement: ControlCategoryFailureDataPoint = {
    type: "control-category-failure",
    id: "no-endpoint-management",
    name: getPartnerEventName(PartnerEvents.NO_ASSET_MANAGER),
    enabled: !(threat && technique),
    category,
    failure: PartnerEvents.NO_ASSET_MANAGER,
    resourceType: "endpoint",
    resourceCount: missingCount,
    controlFailureCount: 0,
    misconfigurationCount: missingCount,
    exceptionCount: 0,
    children: [],
  };

  const endpointManagementItems = getControlsData({ ...options, category });

  const children = [noEndpointManagement, ...endpointManagementItems];

  const endpointCount = getSummaryCount(summary, category, showExcepted);

  const { controlFailureCount, misconfigurationCount } =
    getAggregateCounts(children);

  return {
    type: "control-category",
    id: getCategorySlug(category),
    name: categoryName,
    category,
    enabled: children.some((c) => c.enabled),
    resourceType: "endpoint",
    resourceCount: endpointCount,
    controlFailureCount,
    misconfigurationCount,
    exceptionCount: getCategoryExceptionsCount({
      category,
      objectExceptions,
      policyExceptions,
    }),
    children: [
      ...children,
      {
        type: "coming-soon",
        id: "tanium",
        name: "Tanium",
        enabled: !threat,
        resourceType: "endpoint",
        resourceCount: 0,
        controlFailureCount: 0,
        misconfigurationCount: 0,
        exceptionCount: 0,
        children: [],
      },
    ],
  };
};

const getVulnerabilityManagementBucket = (
  options: FormattedDataOptions,
): DataPoint => {
  const {
    summary,
    objectExceptions,
    policyExceptions,
    threat,
    technique,
    showExcepted,
  } = options;

  const category = ControlCategories.VULN_MANAGER;
  const categoryName = getControlCategoryName(category);
  const id = getCategorySlug(category);

  const missingAgentCount =
    (showExcepted
      ? summary.endpoint_summary.missing_vuln_manager_count
      : summary.endpoint_summary.excepted.missing_vuln_manager_count) ?? 0;

  const missingScanCount =
    (showExcepted
      ? summary.endpoint_summary.missing_vuln_scan_count
      : summary.endpoint_summary.excepted.missing_vuln_scan_count) ?? 0;

  const missingAgent: ControlCategoryFailureDataPoint = {
    type: "control-category-failure",
    id: `${id}-missing-agent`,
    name: getPartnerEventName(PartnerEvents.NO_VULN_MANAGER),
    enabled: !(threat && technique),
    category,
    failure: PartnerEvents.NO_VULN_MANAGER,
    resourceType: "endpoint",
    resourceCount: missingAgentCount,
    controlFailureCount: 0,
    misconfigurationCount: missingAgentCount,
    exceptionCount: 0,
    children: [],
  };

  const missingScan: ControlCategoryFailureDataPoint = {
    type: "control-category-failure",
    id: `${id}-missing-scan`,
    name: getPartnerEventName(PartnerEvents.MISSING_SCAN),
    enabled: !(threat && technique),
    category,
    failure: PartnerEvents.MISSING_SCAN,
    resourceType: "endpoint",
    resourceCount: missingScanCount,
    controlFailureCount: 0,
    misconfigurationCount: missingScanCount,
    exceptionCount: 0,
    children: [],
  };

  const endpointManagementItems = getControlsData({ ...options, category });

  const children = [missingAgent, missingScan, ...endpointManagementItems];

  const endpointCount = getSummaryCount(summary, category, showExcepted);

  const { controlFailureCount, misconfigurationCount } =
    getAggregateCounts(children);

  return {
    type: "control-category",
    id: getCategorySlug(category),
    name: categoryName,
    category,
    enabled: children.some((c) => c.enabled),
    resourceType: "endpoint",
    resourceCount: endpointCount,
    controlFailureCount,
    misconfigurationCount,
    exceptionCount: getCategoryExceptionsCount({
      category,
      objectExceptions,
      policyExceptions,
    }),
    children,
  };
};

const getEmailBucket = (options: FormattedDataOptions): DataPoint => {
  const { summary, objectExceptions, policyExceptions, threat, showExcepted } =
    options;

  const category = ControlCategories.EMAIL;
  const categoryName = getControlCategoryName(category);
  const children = getControlsData({ ...options, category });
  const inboxCount = getSummaryCount(summary, category, showExcepted);

  const { controlFailureCount, misconfigurationCount } =
    getAggregateCounts(children);

  return {
    type: "control-category",
    id: getCategorySlug(category),
    name: categoryName,
    category,
    enabled: children.some((c) => c.enabled),
    resourceType: "inbox",
    resourceCount: inboxCount,
    controlFailureCount,
    misconfigurationCount,
    exceptionCount: getCategoryExceptionsCount({
      category,
      objectExceptions,
      policyExceptions,
    }),
    children: [
      ...children,
      {
        type: "coming-soon",
        id: "abnormal",
        name: "Abnormal Security",
        enabled: !threat,
        resourceType: "user",
        resourceCount: 0,
        controlFailureCount: 0,
        misconfigurationCount: 0,
        exceptionCount: 0,
        children: [],
      },
      {
        type: "coming-soon",
        id: "mimecast",
        name: "Mimecast",
        enabled: !threat,
        resourceType: "user",
        resourceCount: 0,
        controlFailureCount: 0,
        misconfigurationCount: 0,
        exceptionCount: 0,
        children: [],
      },
    ],
  };
};

const getIdentityProvidersBucket = (
  options: FormattedDataOptions,
): DataPoint => {
  const { summary, objectExceptions, policyExceptions, showExcepted } = options;

  const category = ControlCategories.IDENTITY;
  const categoryName = getControlCategoryName(category);
  const children = getControlsData({ ...options, category });
  const userCount = getSummaryCount(summary, category, showExcepted);

  const { controlFailureCount, misconfigurationCount } =
    getAggregateCounts(children);

  return {
    type: "control-category",
    id: getCategorySlug(category),
    name: categoryName,
    category,
    enabled: children.some((c) => c.enabled),
    resourceType: "user",
    resourceCount: userCount,
    controlFailureCount,
    misconfigurationCount,
    exceptionCount: getCategoryExceptionsCount({
      category,
      objectExceptions,
      policyExceptions,
    }),
    children,
  };
};

const getControlCategories = ({
  account,
  summary,
  objectExceptions,
  policyExceptions,
  threat,
  technique,
  showExcepted,
}: FormattedDataOptions): DataPoint[] =>
  [
    getDiscoveredDevicesBucket({
      account,
      summary,
      objectExceptions,
      policyExceptions,
      threat,
      technique,
      showExcepted,
    }),
    getEDRBucket({
      account,
      summary,
      objectExceptions,
      policyExceptions,
      threat,
      technique,
      showExcepted,
    }),
    getEndpointManagementBucket({
      account,
      summary,
      objectExceptions,
      policyExceptions,
      threat,
      technique,
      showExcepted,
    }),
    getVulnerabilityManagementBucket({
      account,
      summary,
      objectExceptions,
      policyExceptions,
      threat,
      technique,
      showExcepted,
    }),
    getEmailBucket({
      account,
      summary,
      objectExceptions,
      policyExceptions,
      threat,
      technique,
      showExcepted,
    }),
    getIdentityProvidersBucket({
      account,
      summary,
      objectExceptions,
      policyExceptions,
      threat,
      technique,
      showExcepted,
    }),
  ].sort((a, b) => a.name.localeCompare(b.name));

const getNoControls = (): DataPoint[] => [
  {
    type: "no-controls",
    id: "no-controls",
    name: "Evaluate your controls by connecting your integrations",
    enabled: true,
    resourceType: "endpoint",
    resourceCount: 0,
    controlFailureCount: 0,
    misconfigurationCount: 0,
    exceptionCount: 0,
    children: [],
  },
];

const getFormattedData =
  (threat?: SCMThreat, technique?: string) =>
  ({
    account,
    summary,
    objectExceptions,
    policyExceptions,
    showExcepted,
  }: {
    account: Account;
    summary: ControlMonitorSummaryResponse;
    objectExceptions: ObjectException[];
    policyExceptions: PolicyException[];
    showExcepted: boolean;
  }): DataPoint[] => {
    const children =
      account.controls.length > 0
        ? getControlCategories({
            account,
            summary,
            objectExceptions,
            policyExceptions,
            threat,
            technique,
            showExcepted,
          })
        : getNoControls();

    const controlFailureCount = sum(children.map((d) => d.controlFailureCount));
    const misconfigurationCount = sum(
      children.map((d) => d.misconfigurationCount),
    );

    return [
      {
        type: "root",
        id: "my-controls",
        name: getRootName(account, threat),
        enabled: !threat || children.some((c) => c.enabled),
        resourceType: "endpoint",
        resourceCount:
          summary.endpoint_summary.endpoint_count +
          summary.inbox_summary.inbox_count +
          summary.user_summary.user_count,
        exceptedResourceCount:
          summary.endpoint_summary.excepted.endpoint_count +
          summary.inbox_summary.excepted.inbox_count +
          summary.user_summary.excepted.user_count,
        totalEndpointCount: summary.endpoint_summary.endpoint_count,
        totalInboxCount: summary.inbox_summary.inbox_count,
        totalUserCount: summary.user_summary.user_count,
        exceptedEndpointCount: summary.endpoint_summary.excepted.endpoint_count,
        exceptedInboxCount: summary.inbox_summary.excepted.inbox_count,
        exceptedUserCount: summary.user_summary.excepted.user_count,
        controlFailureCount,
        misconfigurationCount,
        exceptionCount: 0,
        children,
      },
    ];
  };

function getRootName(account: Account, threat?: SCMThreat) {
  if (threat) {
    return threat.name;
  }

  return account.company
    ? `${capitalize(account.company)}'s Controls`
    : "My Controls";
}

export const useControlMonitorQuery = () => {
  const authConfig = useAuthConfig();
  const router = useRouterState();
  const { showExcepted, exceptedFilters } = useExceptedFilters();

  const threatId = router.location.search.threat_id;
  const technique = router.location.search.technique;
  const filtersParam = router.location.search.filters;

  const filters: TopLevelFilters | undefined =
    filtersParam &&
    Object.fromEntries(
      Object.keys(filtersParam).map((key) => [
        key,
        [
          ...(filtersParam[key as keyof typeof filtersParam] ?? []),
          ...exceptedFilters,
        ],
      ]),
    );

  return useQuery({
    ...aggregateTechniqueSummaryQuery(authConfig, {
      threatId,
      focusTechnique: technique,
      filters,
      showExcepted,
    }),
    placeholderData: (data) => data,
  });
};

export type ControlMonitorSummary =
  | ControlMonitorEndpointControlSummary
  | ControlMonitorUserControlSummary
  | ControlMonitorInboxControlSummary;

export interface TechniqueSummary {
  id: string;
  name: string;
  misconfigurationCount?: number;
}

export interface AggregateTechniqueSummary {
  threat?: SCMThreat;
  mostConcerningControlFailureNode: DataPoint | undefined;
  summary: ControlMonitorSummaryResponse;
  dataPoints: DataPoint[];
}

export function individualTechniqueSummaryQuery(
  queryClient: QueryClient,
  authConfig: AuthConfig,
  techniquesIds: string[],
) {
  return queryOptions({
    queryKey: [
      authConfig,
      "scm",
      "individual-summaries",
      techniquesIds,
    ] as const,

    queryFn: async (): Promise<TechniqueSummary[]> => {
      const techniquesPromise = queryClient.ensureQueryData(
        queries.detect.techniques(authConfig),
      );

      const techniqueSummariesPromises = queryClient.ensureQueryData(
        queries.scm.techniqueSummaries(authConfig, techniquesIds),
      );

      const [techniques, techniqueSummaries] = await Promise.all([
        techniquesPromise,
        techniqueSummariesPromises,
      ]);

      return techniquesIds.map((techniqueId) => {
        const summary = techniqueSummaries.find(
          (s) => s.technique === techniqueId,
        );

        const misconfigurationCount = summary?.instances.reduce(
          (acc, c) => acc + (c.setting_misconfiguration_count ?? 0),
          0,
        );

        return {
          id: techniqueId,
          name: techniques.find((t) => t.id === techniqueId)?.name ?? "",
          misconfigurationCount: misconfigurationCount,
        };
      });
    },
    gcTime: 0, // Disable caching
  });
}

export function aggregateTechniqueSummaryQuery(
  authConfig: AuthConfig,
  {
    threatId,
    focusTechnique,
    filters,
    showExcepted,
  }: {
    threatId?: string;
    focusTechnique?: string;
    filters?: TopLevelFilters;
    showExcepted: boolean;
  },
) {
  return queryOptions({
    queryKey: [
      authConfig,
      "scm",
      "aggregate-summary",
      threatId,
      focusTechnique,
      filters,
      showExcepted,
    ] as const,
    queryFn: async (): Promise<AggregateTechniqueSummary> => {
      const threats = await service(authConfig).scm.listThreats();

      const threat = threatId
        ? threats.find((t) => t.id === threatId)
        : undefined;

      const accountPromise = service(authConfig).iam.getAccount();

      const summaryPromise = service(authConfig).scm.getControlMonitorSummary({
        endpointsQuery: filters?.endpoints
          ? {
              filter: {
                and: [...getOdataFilters(filters.endpoints)],
              },
            }
          : undefined,
        usersQuery: filters?.users
          ? {
              filter: {
                and: [...getOdataFilters(filters.users)],
              },
            }
          : undefined,
        inboxesQuery: filters?.inboxes
          ? {
              filter: {
                and: [...getOdataFilters(filters.inboxes)],
              },
            }
          : undefined,
        techniques: focusTechnique
          ? [focusTechnique]
          : (threat?.techniques ?? []),
      });

      const objectExceptionsPromise =
        service(authConfig).scm.objectExceptions();

      const policyExceptionsPromise =
        service(authConfig).scm.policyExceptions();

      const [account, summary, objectExceptions, policyExceptions] =
        await Promise.all([
          accountPromise,
          summaryPromise,
          objectExceptionsPromise,
          policyExceptionsPromise,
        ]);

      const formattedData = getFormattedData(threat, focusTechnique);
      const data = formattedData({
        account,
        summary,
        objectExceptions,
        policyExceptions,
        showExcepted,
      });

      const mostConcerningControlFailureNode =
        findMostConcerningControlFailureNode(data);

      return {
        threat,
        summary,
        mostConcerningControlFailureNode,
        dataPoints: data,
      };
    },
  });
}

export function useAggregateTechniqueSummary({
  threatId,
  focusTechnique,
}: {
  threatId?: string;
  focusTechnique?: string;
}) {
  const authConfig = useAuthConfig();
  const router = useRouterState();
  const { showExcepted } = useExceptedFilters();
  const filters = router.location.search.filters;

  return useQuery({
    ...aggregateTechniqueSummaryQuery(authConfig, {
      threatId,
      focusTechnique,
      filters,
      showExcepted,
    }),
    enabled: !!threatId,
  });
}

function flattenData(data: DataPoint): DataPoint[] {
  const children = data.children.reduce((acc: DataPoint[], current) => {
    acc.push(current, ...current.children.flatMap((c) => flattenData(c)));
    return acc;
  }, []);
  return [data, ...children];
}

export function findNodeWithMostFailures<T extends DataPoint>(
  data: T[],
): T | undefined {
  return data.reduce((result: T | undefined, current) => {
    if (
      result &&
      result.misconfigurationCount <= current.misconfigurationCount
    ) {
      return result;
    }
    return current;
  }, undefined);
}

export function findNodeWithMostControlFailures<T extends DataPoint>(
  data: T[],
): T | undefined {
  return data.reduce((result, current) => {
    if (result.controlFailureCount > current.controlFailureCount) {
      return result;
    }
    return current;
  });
}

function getControlItems(data: DataPoint[]) {
  const controlItems = data.reduce(
    (result: ConnectedControlDataPoint[], item) => {
      if (item.type === "connected-control") {
        result.push(item);
      }
      result.push(...getControlItems(item.children));
      return result;
    },
    [],
  );
  return controlItems;
}

// Will try to find the most relevant node before finding the most relevant control
export function findMostRelevantNode(data: DataPoint[]) {
  const mostFailuresNode = findNodeWithMostFailures(data);
  if (mostFailuresNode && mostFailuresNode.misconfigurationCount > 0) {
    return mostFailuresNode;
  }

  return findMostRelevantControl(data);
}

export function findMostRelevantControl(data: DataPoint[]) {
  const controlItems = getControlItems(data);
  const mostFailuresControl = findNodeWithMostFailures(controlItems);

  if (mostFailuresControl && mostFailuresControl.misconfigurationCount > 0) {
    return mostFailuresControl;
  }

  const mostSettingsControl = findControlWithMostSettings(controlItems);

  if (mostSettingsControl && mostSettingsControl.settingsCount > 0) {
    return mostSettingsControl;
  }

  return undefined;
}

function findMostConcerningControlFailureNode(data: DataPoint[]) {
  const mostFailuresControl = findNodeWithMostControlFailures(data);
  if (!mostFailuresControl) {
    return undefined;
  }

  const items = flattenData(mostFailuresControl);
  return items
    .filter((i) => i.children.length === 0)
    .reduce((result, current) => {
      if (result.controlFailureCount > current.controlFailureCount) {
        return result;
      }
      return current;
    });
}

function findControlWithMostSettings(
  controlItems: ConnectedControlDataPoint[],
): ConnectedControlDataPoint | undefined {
  return controlItems.reduce(
    (result: ConnectedControlDataPoint | undefined, current) => {
      if (result && result.settingsCount <= current.settingsCount) {
        return result;
      }
      return current;
    },
    undefined,
  );
}

export const useTopLevelFilters = (): TopLevelFilters => {
  const router = useRouterState();
  return router.location.search.filters ?? {};
};

export const useTopLevelFiltersFor = (category: Category): Filter[] => {
  const filters = useTopLevelFilters();
  return filters[category] ?? [];
};

export const useTopLevelFiltersQuery = <T>(
  category: Category,
): ODataQuery<T> | undefined => {
  const filters = useTopLevelFilters();
  const categoryFilters = filters[category];

  if (!categoryFilters || categoryFilters?.length === 0) {
    return undefined;
  }

  return {
    filter: {
      and: [...getOdataFilters(categoryFilters)],
    },
  };
};

export const useExceptedFilters = (category?: ValidControlCategory) => {
  const { visibility: showExcepted, setVisibility: setShowExcepted } =
    useExceptionVisibilityStore();

  const exceptedFilters: Filter[] = showExcepted
    ? []
    : [
        {
          ...exceptedOdata,
          operator: "is",
          value: false,
          with: { category },
        },
      ];

  return { exceptedFilters, showExcepted, setShowExcepted };
};
